// Fase 9B — Voice softphone provider (Twilio Voice JS SDK). Un solo Device.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Device } from "@twilio/voice-sdk";
import { toast } from "sonner";
import { Phone, PhoneOff, Mic, MicOff, Store } from "lucide-react";
import { api, createAgencyClient } from "@/lib/api";
import { ApiProvider } from "@/lib/apiClient";
import CallbackModal from "@/components/CallbackModal";
import QuickAppointmentModal from "@/components/QuickAppointmentModal";

const VoiceCtx = createContext(null);
export const useVoice = () => useContext(VoiceCtx) || { startCall: () => {}, busy: false, state: "idle" };

function fmt(s) { const m = Math.floor(s / 60); const r = s % 60; return `${m}:${String(r).padStart(2, "0")}`; }

const OUTCOME_OPTS = [
  { key: "booked", label: "Appuntamento fissato" },
  { key: "callback_again", label: "Da richiamare" },
  { key: "no_answer", label: "Non risponde / non raggiunto" },
  { key: "not_interested", label: "Non interessato" },
  { key: "other", label: "Altro" },
];

export function VoiceProvider({ children }) {
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);
  const [state, setState] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [ctx, setCtx] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [showCb, setShowCb] = useState(false);
  const [showAppt, setShowAppt] = useState(false);

  const stopTimer = () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; };
  const reset = () => { callRef.current = null; stopTimer(); setMuted(false); setState("idle"); setCtx(null); };

  const ensureDevice = useCallback(async () => {
    if (deviceRef.current) return deviceRef.current;
    const r = await api.get("/agency/voice/token");
    const dev = new Device(r.data.token, { codecPreferences: ["opus", "pcmu"] });
    dev.on("tokenWillExpire", async () => {
      try { const rr = await api.get("/agency/voice/token"); dev.updateToken(rr.data.token); } catch { /* noop */ }
    });
    await dev.register();
    deviceRef.current = dev;
    return dev;
  }, []);

  const finish = useCallback((logId) => {
    stopTimer();
    const lid = logId || ctx?.call_log_id;
    setState("ended");
    if (lid) setOutcome({ call_log_id: lid, tenant_id: ctx?.tenant_id, client_id: ctx?.client_id,
      clientName: ctx?.clientName, tenantName: ctx?.tenantName, agency_case_id: ctx?.agency_case_id });
    reset();
  }, [ctx]);

  const startCall = useCallback(async (target) => {
    if (callRef.current || state !== "idle") { toast.error("Hai già una chiamata in corso"); return; }
    try { await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { toast.error("Permesso microfono negato: impossibile chiamare"); return; }
    setState("connecting"); setCtx(target);
    let sess;
    try {
      const r = await api.post("/agency/voice/call-sessions", {
        client_id: target.client_id, tenant_id: target.tenant_id,
        agency_case_id: target.agency_case_id, callback_id: target.callback_id });
      sess = r.data;
      setCtx((c) => ({ ...c, call_log_id: sess.call_log_id,
        clientName: c?.clientName || sess.client_name, tenantName: c?.tenantName || sess.tenant_name }));
    } catch (e) {
      reset();
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === "string" ? d : null))) || "Chiamata non avviata");
      return;
    }
    try {
      const dev = await ensureDevice();
      const call = await dev.connect({ params: { call_session_id: sess.call_session_id } });
      callRef.current = call;
      setState("ringing");
      call.on("accept", () => { setState("connected"); setSeconds(0); stopTimer(); timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000); });
      call.on("disconnect", () => finish(sess.call_log_id));
      call.on("cancel", () => finish(sess.call_log_id));
      call.on("error", () => { toast.error("Errore durante la chiamata"); finish(sess.call_log_id); });
    } catch {
      toast.error("Voice non disponibile");
      finish(sess.call_log_id);
    }
  }, [ensureDevice, state, finish]);

  const hangup = () => { if (callRef.current) { try { callRef.current.disconnect(); } catch { /* noop */ } } };
  const toggleMute = () => { if (callRef.current) { const m = !muted; try { callRef.current.mute(m); setMuted(m); } catch { /* noop */ } } };

  useEffect(() => () => { stopTimer(); if (deviceRef.current) { try { deviceRef.current.destroy(); } catch { /* noop */ } } }, []);

  const recordOutcome = async (key) => {
    if (!outcome) return;
    try { await api.post(`/agency/voice/call-logs/${outcome.call_log_id}/outcome`, { outcome: key }); }
    catch { /* best-effort */ }
  };
  const submitOutcome = async (key) => {
    if (!outcome) return;
    // "Appuntamento fissato" e "Da richiamare" contano SOLO se l'azione viene
    // davvero salvata: apro il modal e registro l'esito solo nell'onSaved.
    if (key === "callback_again") { setShowCb(true); return; }
    if (key === "booked") { setShowAppt(true); return; }
    await recordOutcome(key);
    toast.success("Esito registrato"); setOutcome(null);
  };

  const busy = state !== "idle" && state !== "ended";
  const tenantClient = outcome ? createAgencyClient(() => outcome.tenant_id) : null;

  return (
    <VoiceCtx.Provider value={{ startCall, busy, state }}>
      {children}

      {busy && ctx && (
        <div data-testid="voice-call-bar" className="fixed bottom-4 right-4 z-[80] w-72 rounded-2xl bg-[#0f172a] text-white shadow-2xl p-4">
          <div className="font-black text-lg truncate">{ctx.clientName || "Contatto"}</div>
          <div className="text-xs text-white/70 flex items-center gap-1"><Store className="h-3 w-3" /> {ctx.tenantName || "—"}</div>
          <div className="mt-2 text-sm text-white/80" data-testid="voice-call-state">
            {state === "connecting" && "Connessione…"}
            {state === "ringing" && "Sta squillando…"}
            {state === "connected" && <span className="font-mono">{fmt(seconds)}</span>}
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="voice-mute-btn" onClick={toggleMute} disabled={state !== "connected"}
              className="flex-1 rounded-lg bg-white/10 hover:bg-white/20 py-2 flex items-center justify-center gap-1 text-sm disabled:opacity-40">
              {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}{muted ? "Riattiva" : "Muto"}
            </button>
            <button data-testid="voice-hangup-btn" onClick={hangup}
              className="flex-1 rounded-lg bg-rose-600 hover:bg-rose-700 py-2 flex items-center justify-center gap-1 text-sm">
              <PhoneOff className="h-4 w-4" /> Termina
            </button>
          </div>
        </div>
      )}

      {outcome && !showCb && !showAppt && (
        <div className="fixed inset-0 z-[85] bg-black/40 flex items-center justify-center p-4" data-testid="voice-outcome-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5">
            <h3 className="font-display text-xl font-black mb-1">Esito chiamata</h3>
            <p className="text-xs text-[color:var(--sp-text-muted)] mb-3">{outcome.clientName} · {outcome.tenantName}</p>
            <div className="space-y-2">
              {OUTCOME_OPTS.map((o) => (
                <button key={o.key} data-testid={`voice-outcome-${o.key}`} onClick={() => submitOutcome(o.key)}
                  className="w-full text-left rounded-lg border border-[color:var(--sp-border)] px-3 py-2.5 hover:bg-[color:var(--sp-subtle)] text-sm font-semibold">{o.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {outcome && showCb && tenantClient && (
        <ApiProvider client={tenantClient}>
          <CallbackModal tenant={{ id: outcome.tenant_id, name: outcome.tenantName }} clientId={outcome.client_id}
            clientName={outcome.clientName} caseObj={outcome.agency_case_id ? { id: outcome.agency_case_id } : null}
            onClose={() => { setShowCb(false); setOutcome(null); }}
            onSaved={async () => { await recordOutcome("callback_again"); setShowCb(false); setOutcome(null); toast.success("Richiamo programmato"); }} />
        </ApiProvider>
      )}

      {outcome && showAppt && tenantClient && (
        <ApiProvider client={tenantClient}>
          <QuickAppointmentModal clientId={outcome.client_id}
            onClose={() => { setShowAppt(false); setOutcome(null); }}
            onSaved={async () => { await recordOutcome("booked"); setShowAppt(false); setOutcome(null); toast.success("Appuntamento creato"); }} />
        </ApiProvider>
      )}
    </VoiceCtx.Provider>
  );
}

// CTA riutilizzabile. Il backend determina Caller ID/destinazione (anti-spoofing)
// ed è comunque autoritativo: questa readiness è solo UX.
export function CallButton({ target, className = "", label = "Chiama", compact = false, testid = "voice-call-btn" }) {
  const { startCall, busy } = useVoice();
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!target?.tenant_id) return undefined;
    api.get("/agency/voice/call-readiness", {
      params: { tenant_id: target.tenant_id, client_id: target.client_id },
    }).then((r) => { if (alive) setReadiness(r.data); })
      .catch(() => { if (alive) setReadiness(null); });
    return () => { alive = false; };
  }, [target?.tenant_id, target?.client_id]);

  const blocked = readiness && readiness.can_call === false;

  const onClick = (e) => {
    e.stopPropagation();
    if (!blocked) { startCall(target); return; }
    if (readiness.action === "configure_my_voice") {
      toast.error(readiness.message, {
        action: { label: "Configura telefonia",
                  onClick: () => navigate("/admin/agency/impostazioni") },
      });
      return;
    }
    toast.error(readiness.message);
  };

  return (
    <button data-testid={testid} disabled={busy} onClick={onClick}
      data-call-blocked={blocked ? readiness.reason : undefined}
      className={`${className || `sp-btn-ghost inline-flex items-center gap-2 justify-center disabled:opacity-40 ${compact ? "!p-2" : ""}`}${blocked ? " opacity-40" : ""}`}
      title={blocked ? readiness.message : "Chiama"}>
      <Phone className="h-4 w-4" />{!compact && label}
    </button>
  );
}
